import config from "common/config";
import {myFirebase} from "../firebase/firebase";
import {isEmpty} from "lodash";

const {API_BASE_URL} = config;
const baseUrl = API_BASE_URL.replace(/http/, 'ws')
  .replace(/https/, 'wss');

const API_PATH = baseUrl + '/ws/chat/';

/**
 * WebSocket service for real-time chat communication.
 * Manages WebSocket connections, automatic reconnection with exponential backoff,
 * and provides a fluent API for chat operations.
 *
 * @pattern Method Chaining - All public action methods return `this` to enable fluent API usage:
 *   WebSocketService.getInstance()
 *     .connect('user123', channels)
 *     .addCallbacks(onMessages, onNewMessage, onUnread, onTimestamp)
 *     .newChatMessage({ text: 'Hello', channelId: '123' });
 *
 * Internal/utility methods (getToken, waitForSocketReady, etc.) do not return `this`
 * as they are not intended for external chaining.
 */
export class WebSocketService {
  static instance = null;
  static SOCKET_READY_TIMEOUT_MS = 1000;
  static SOCKET_CONNECTION_TIMEOUT_MS = 5000;

  // WebSocket readyState constants for readability
  static WEBSOCKET_CONNECTING = 0;
  static WEBSOCKET_OPEN = 1;
  static WEBSOCKET_CLOSING = 2;
  static WEBSOCKET_CLOSED = 3;

  // Reconnection constants
  static MAX_RECONNECT_ATTEMPTS = 5;
  static INITIAL_RECONNECT_DELAY_MS = 1000;
  static MAX_RECONNECT_DELAY_MS = 30000;

  callbacks = {};

  /**
   * Creates a new WebSocket service instance.
   * Use getInstance() for singleton pattern.
   */
  constructor() {
    this.socketRef = null;
    this.previousChannelCount = 0;
    this.chatChannels = null;
    this.reconnectAttempts = 0;
    this.intentionalDisconnect = false;
    this.messageQueue = [];

    // Bind event handlers to prevent memory leaks
    this.handleSocketOpen = this.handleSocketOpen.bind(this);
    this.handleSocketMessage = this.handleSocketMessage.bind(this);
    this.handleSocketError = this.handleSocketError.bind(this);
    this.handleSocketClose = this.handleSocketClose.bind(this);
  }

  /**
   * Gets the singleton instance of the WebSocket service.
   * @returns {WebSocketService} The singleton instance
   */
  static getInstance() {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  /**
   * Retrieves a fresh Firebase authentication token.
   * @returns {Promise<string>} Firebase ID token
   * @throws {Error} If user is not authenticated
   */
  async getToken() {
    const user = myFirebase.auth().currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    return await user.getIdToken();
  }

  /**
   * Handles WebSocket open event.
   */
  handleSocketOpen() {
    // console.debug('Socket: WebSocket open');
    this.reconnectAttempts = 0; // Reset on successful connection
    (this.chatChannels || []).map(channel => this.joinChannel(channel.channelId));
    this.getUnreadMessagesCount();
  }

  /**
   * Handles WebSocket message event.
   * @param {MessageEvent} e - WebSocket message event
   */
  handleSocketMessage(e) {
    this.socketNewMessage(e.data);
  }

  /**
   * Handles WebSocket error event.
   * @param {Event} e - WebSocket error event
   */
  handleSocketError(e) {
    console.debug('Socket: error in socket connection', e.message);
  }

  /**
   * Handles WebSocket close event with reconnection logic.
   */
  handleSocketClose() {
    // console.debug("Socket: WebSocket closed let's reopen if user is still logged in");
    if (this.intentionalDisconnect) {
      this.intentionalDisconnect = false;
      return;
    }

    if (myFirebase.auth().currentUser && this.chatChannels && this.chatChannels.length > 0) {
      if (this.reconnectAttempts < WebSocketService.MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(
          WebSocketService.INITIAL_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts),
          WebSocketService.MAX_RECONNECT_DELAY_MS
        );
        setTimeout(() => {
          this.reconnectAttempts++;
          this.connect(this.nickName, this.chatChannels);
        }, delay);
      } else {
        console.error('WebSocket: Max reconnection attempts reached. Chat unavailable.');
        if (this.callbacks['connection_failed']) {
          this.callbacks['connection_failed']();
        }
      }
    }
  }

  /**
   * Establishes or reuses a WebSocket connection for the given channels.
   * Automatically handles reconnection with exponential backoff.
   * @param {string} nickName - User nickname
   * @param {Array} chatChannels - Array of channel objects
   * @returns {WebSocketService} This instance for method chaining
   */
  connect(nickName, chatChannels) {
    this.nickName = nickName;
    this.chatChannels = chatChannels;
    if (this.socketRef &&
        (this.socketRef.readyState === WebSocketService.WEBSOCKET_OPEN ||
         this.socketRef.readyState === WebSocketService.WEBSOCKET_CONNECTING) &&
        this.previousChannelCount === chatChannels.length) {
      return this;
    }

    // Close existing socket before creating new one to prevent resource leaks
    if (this.socketRef && this.socketRef.readyState !== WebSocketService.WEBSOCKET_CLOSED) {
      this.socketRef.close();
    }

    this.previousChannelCount = chatChannels.length;
    this.socketRef = new WebSocket(API_PATH);
    this.socketRef.onopen = this.handleSocketOpen;
    this.socketRef.onmessage = this.handleSocketMessage;
    this.socketRef.onerror = this.handleSocketError;
    this.socketRef.onclose = this.handleSocketClose;
    return this;
  }

  /**
   * Closes the current WebSocket connection.
   * @returns {WebSocketService} This instance for method chaining
   */
  close() {
    if (this.socketRef) {
      this.intentionalDisconnect = true;
      this.socketRef.close();
    }
    return this;
  }

  /**
   * Handles incoming WebSocket messages and dispatches to registered callbacks.
   * @param {string} data - JSON string containing message data
   * @returns {WebSocketService} This instance for method chaining
   */
  socketNewMessage(data) {
    // console.debug('Socket: received message ', data);
    const parsedData = JSON.parse(data);
    const command = parsedData.command;
    if (Object.keys(this.callbacks).length === 0) {
      return;
    }
    if (command === 'messages_payload') {
      this.callbacks[command](parsedData.messages, parsedData.new_page_number, parsedData.type);
    }
    if (command === 'update_last_viewed_timestamp') {
      this.callbacks[command](parsedData.data);
    }
    if (command === 'new_message' || command === 'unread_message' || command === 'reload') {
      this.callbacks[command](parsedData.messages);
    }
    return this;
  }

  async getUnreadMessagesCount() {
    await this.sendMessage({command: 'unread_message'});
    return this;
  }

  async fetchMessages() {
    await this.sendMessage({command: 'fetch_messages'});
    return this;
  }

  async joinAndFetchMessagesForChannel(channelId, pageNumber, messageId) {
    await this.joinChannel(channelId);
    await this.fetchMessagesForChannel(channelId, pageNumber, messageId);
    this.channelId = channelId;
    return this;
  }

  async fetchMessagesForChannel(channelId, pageNumber, messageId) {
    await this.sendMessage({command: 'get_room_chat_messages', channel: channelId, page_number: pageNumber, message_id: messageId});
    this.channelId = channelId;
    return this;
  }

  /**
   * Sends a new chat message to a specific channel.
   * @param {Object} message - Message object
   * @param {string} message.text - Message content
   * @param {string} message.channelId - Target channel ID
   * @param {Array} [message.attachments] - File attachments
   * @param {string} [message.tempId] - Temporary message ID for tracking
   * @returns {WebSocketService} This instance for method chaining
   */
  async newChatMessage(message) {
    await this.sendMessage({command: 'send', message: message.text, channel: message.channelId, attachments: message.attachments, tempId: message.tempId});
    return this;
  }

  async joinChannel(channelId) {
    // console.debug('Socket: joining channel ', channelId);
    await this.sendMessage({command: 'join', channel: channelId});
    return this;
  }

  async leaveChannel(channelId) {
    // console.debug('Socket: leaving channel ', channelId);
    await this.sendMessage({command: 'leave', channel: channelId});
    return this;
  }


  /**
   * Registers callback functions for different WebSocket message types.
   * @param {Function} messagesCallback - Handler for message payload responses
   * @param {Function} newMessageCallback - Handler for new messages
   * @param {Function} unreadMessagesCallback - Handler for unread message counts
   * @param {Function} updateLastViewedTime - Handler for timestamp updates
   * @returns {WebSocketService} This instance for method chaining
   *
   * Additional callbacks can be registered with addCallback():
   * - 'connection_failed': Called when max reconnection attempts are reached
   * - 'send_error': Called when message sending fails
   */
  addCallbacks(messagesCallback, newMessageCallback, unreadMessagesCallback, updateLastViewedTime) {
    this.callbacks['messages_payload'] = messagesCallback;
    this.callbacks['new_message'] = newMessageCallback;
    this.callbacks['unread_message'] = unreadMessagesCallback;
    this.callbacks['update_last_viewed_timestamp'] = updateLastViewedTime;
    return this;
  }

  /**
   * Registers a callback function for a specific command type.
   * @param {string} command - Command type to listen for
   * @param {Function} callback - Handler function for the command
   * @returns {WebSocketService} This instance for method chaining
   */
  addCallback(command, callback) {
    this.callbacks[command] = callback;
    return this;
  }

  async handleAttachmentData(data, messageWithAttachment) {
    const attachmentsData = []
    // Map attachments to an array of Promises for file reading
    const fileReadPromises = data.attachments.map((attachment) => {
      if (attachment?.url) {
        return attachmentsData.push({
          name: attachment.name,
          type: attachment.type,
          url: attachment.url,
        });
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          attachmentsData.push({
            name: attachment.name,
            type: attachment.type,
            data: reader.result.split(',')[1], // Extracting base64 data from the result
          });
          resolve();
        };

        // Handle errors during file reading
        reader.onerror = (error) => {
          reject(error);
        };

        reader.readAsDataURL(attachment);
      });
    });

    // Wait for all file reading promises to resolve before sending the message
    await Promise.all(fileReadPromises);

    messageWithAttachment.attachments = attachmentsData;
  }

  /**
   * Sends a message through the WebSocket connection.
   * Handles authentication, attachment processing, and ensures connection readiness.
   * @param {Object} data - Message data to send
   * @param {string} data.command - Command type (e.g., 'send', 'join', etc.)
   * @param {string} [data.channel] - Channel ID for channel-specific commands
   * @param {Array} [data.attachments] - Array of file attachments
   * @returns {WebSocketService} This instance for method chaining. Send
   * failures are reported through the 'send_error' callback, never thrown:
   * every caller is fire-and-forget.
   */
  async sendMessage(data) {
    try {
      await this.ensureSocketConnection();

      const idToken = await this.getToken();
      const messageWithAttachment = {
        ...data,
        authToken: idToken,
        nickName: this.nickName,
        attachments: [], // Replace attachment with its metadata
      };
      if (isEmpty(data.attachments)) {
        this.socketRef.send(JSON.stringify(messageWithAttachment));
        return this;
      }

      await this.handleAttachmentData(data, messageWithAttachment)

      this.socketRef.send(JSON.stringify(messageWithAttachment));
      return this;
    } catch (err) {
      console.error('Socket: error sending message', {
        command: data?.command,
        channel: data?.channel,
        error: err?.message || err,
      });

      // Notify UI layer of send failure
      if (this.callbacks['send_error']) {
        this.callbacks['send_error'](data, err);
      }

      return this;
    }
  }

  /**
   * Waits for the WebSocket to be in OPEN state using event-driven approach.
   * Includes timeout protection and race condition handling.
   * @param {number} [additionalWaitTime=1000] - Timeout in milliseconds
   * @returns {Promise<void>} Resolves when socket is ready
   * @throws {Error} If socket is closed or timeout occurs
   */
  async waitForSocketReady(additionalWaitTime = WebSocketService.SOCKET_READY_TIMEOUT_MS) {
    // Helper to wait for socket to be ready right before sending using
    // WebSocket events instead of polling whenever possible.
    if (!this.socketRef) {
      throw new Error('Socket not initialized. Please connect first.');
    }

    // Immediate check if already ready to avoid an unnecessary initial delay
    if (this.socketRef.readyState === WebSocketService.WEBSOCKET_OPEN) {
      return;
    }

    // If the socket is already closed, we cannot wait for it to become ready.
    if (this.socketRef.readyState === WebSocketService.WEBSOCKET_CLOSED) {
      throw new Error('Socket is closed. Please reconnect.');
    }

    const socket = this.socketRef;

    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Socket is not ready to send messages'));
      }, additionalWaitTime);

      const handleOpen = () => {
        cleanup();
        resolve();
      };

      const handleClose = () => {
        cleanup();
        reject(new Error('Socket was closed during connection wait.'));
      };

      const cleanup = () => {
        clearTimeout(timeout);
        socket.removeEventListener('open', handleOpen);
        socket.removeEventListener('close', handleClose);
      };

      socket.addEventListener('open', handleOpen, { once: true });
      socket.addEventListener('close', handleClose, { once: true });

      // Check again in case state changed while adding listeners
      if (this.socketRef.readyState === WebSocketService.WEBSOCKET_OPEN) {
        cleanup();
        resolve();
      }
    });
  }

  /**
   * Ensures the WebSocket connection is ready for sending messages.
   * Uses a longer timeout suitable for initial connection establishment.
   * @returns {Promise<void>} Resolves when socket connection is ready
   * @throws {Error} If socket is not initialized or fails to connect
   */
  async ensureSocketConnection() {
    // Ensure a socket exists before attempting to wait on its state
    if (!this.socketRef) {
      throw new Error('Socket not initialized. Please connect first.');
    }

    // Reuse the generic waitForSocketReady logic with a longer timeout
    // for establishing/ensuring the connection.
    await this.waitForSocketReady(WebSocketService.SOCKET_CONNECTION_TIMEOUT_MS);
  }

  async onMemberMessageSeen(channelId) {
    await this.sendMessage({ command: "message_seen", channel: channelId });
    return this;
  }
}

let WebSocketInstance;
export const initChatInstance = () => {
  WebSocketInstance = WebSocketService.getInstance();
};


export const getChatInstance = () => WebSocketInstance;
